"use client";

import { ApolloProvider } from "@apollo/client/react";
import { Fragment, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { WidgetInstance } from "@/lib/apollo-client";
import { WidgetInstanceProvider } from "@/lib/widget-instance-context";

/**
 * How long a key that went empty / undefined is still treated as the same widget. Host configs that
 * load asynchronously (a framework re-rendering `publicKey={cfg?.key}`, an attribute removed and set
 * again in the same task or a few frames later) blank the key for one render or one fetch tick; that
 * must not tear down an open chat. Long enough to ride out a microtask / a few animation frames / a
 * quick store hydrate, short enough that deliberately removing the key (a Disconnect to demo) shows the
 * demo almost at once. An empty key for longer is a real change.
 */
export const KEY_GRACE_MS = 400;

let nextGeneration = 0;

/** Applies the embed's preview inputs to an instance: the token from the prop / attribute, or, for an
 *  embed that is told its token over postMessage (the iframe), the "hold until it arrives" flag. */
function stampPreview(
  instance: WidgetInstance,
  previewToken: string | null | undefined,
  previewExpected: boolean,
): void {
  instance.preview.setExpected(previewExpected);
  if (!previewExpected) instance.preview.setToken(previewToken ?? null);
}

interface Slot {
  key: string | null;
  isolated: boolean;
  namespace: string | null;
  instance: WidgetInstance;
  /** Identity of the subtree: a new value remounts everything below. */
  generation: number;
}

/**
 * One widget's connection state for the integration `publicKey`: its instance (key, visitor token,
 * 401 recovery) and its Apollo client, provided to the tree below.
 *
 * Key policy (documented in API_REFERENCE.md):
 * - A -> B (another real key) rebuilds the instance and remounts the subtree at once. A key switch is
 *   a different integration and a different visitor, and no `shutdown` event announces it: reusing the
 *   instance would keep A's token, Apollo cache and in-memory session alive under B's requests.
 *   A -> B -> A builds a new instance for A as well (its storage is key-scoped and is read again).
 * - First mount without a key (demo) and then the real key is a normal first mount: the demo instance
 *   never held a token, so it just learns its key and the subtree stays mounted.
 * - A -> empty / undefined keeps A for `KEY_GRACE_MS`; A again within that window changes nothing.
 *   After it, the widget tears down to demo (a real change).
 * The key is stamped while rendering so the first request cannot race an effect.
 */
export function WidgetInstanceScope({
  publicKey,
  isolated: isolatedProp = false,
  previewToken,
  previewExpected = false,
  persistent = false,
  storageNamespace,
  children,
}: {
  publicKey: string | null | undefined;
  /** Private preview sessions: the instance keeps all visitor state in memory (see `WidgetInstance`).
   *  Changing it builds a fresh instance and remounts the subtree. */
  isolated?: boolean;
  /** BE preview token (BE 062), dashboard only. A token FORCES isolation (unless `persistent`): the visitor id, session,
   *  outbox and drafts live in memory. A different value keeps the instance (the memory visitor id
   *  must survive a refresh); adding or removing one builds a new instance. */
  previewToken?: string | null;
  /** The token will arrive over postMessage (iframe preview): isolate now and hold every BE call
   *  until it does. */
  previewExpected?: boolean;
  /** An explicit `session="persistent"`: a preview token no longer forces isolation, so the visitor
   *  id and the rest of the session live in browser storage like a normal site. */
  persistent?: boolean;
  /** Private: prefix of every visitor-scoped storage key (see `WidgetInstance`). A change rebuilds. */
  storageNamespace?: string;
  children: ReactNode;
}) {
  const isolated = isolatedProp || ((!!previewToken || previewExpected) && !persistent);
  const namespace = storageNamespace || null;
  const incoming = publicKey || null;
  // The last real key, kept while the incoming key is empty; cleared when the grace window ends.
  const [held, setHeld] = useState<string | null>(incoming);
  const key = incoming ?? held;

  useEffect(() => {
    if (incoming !== null) {
      setHeld(incoming);
      return;
    }
    const timer = setTimeout(() => setHeld(null), KEY_GRACE_MS);
    return () => clearTimeout(timer);
  }, [incoming]);

  // A ref, not `useMemo`: React may drop a memo and silently remount the whole widget. The update is
  // idempotent (a pure function of the previous slot and `key`), so a double render is harmless.
  const slotRef = useRef<Slot | null>(null);
  const prev = slotRef.current;
  if (!prev || prev.key !== key || prev.isolated !== isolated || prev.namespace !== namespace) {
    if (prev && prev.key === null && prev.isolated === isolated && prev.namespace === namespace) {
      // demo -> first real key: keep the instance and the subtree, only stamp the key.
      prev.instance.publicKey = key;
      slotRef.current = { ...prev, key };
    } else {
      slotRef.current = {
        key,
        isolated,
        namespace,
        instance: new WidgetInstance(key, { isolated, storageNamespace: namespace ?? undefined }),
        generation: nextGeneration++,
      };
      // Before any child renders: the very first request already carries the token.
      stampPreview(slotRef.current.instance, previewToken, previewExpected);
    }
  }
  const { instance, generation } = slotRef.current as Slot;
  // Later changes (a refreshed token) notify subscribers, so they happen in a layout effect, which
  // still runs before every child's passive effect (handshake, config, session content).
  useLayoutEffect(() => {
    stampPreview(instance, previewToken, previewExpected);
  }, [instance, previewToken, previewExpected]);

  return (
    <Fragment key={generation}>
      <WidgetInstanceProvider instance={instance}>
        <ApolloProvider client={instance.client}>{children}</ApolloProvider>
      </WidgetInstanceProvider>
    </Fragment>
  );
}
